import { Autocomplete, Chip } from '@mui/material';
import { Close } from '@mui/icons-material';
import { SMSAutoResponse } from 'domains/content/types';
import TextField from 'domains/core/components/TextField';
import { greyChipStyles } from 'domains/core/components/Chip/Chip';
import InputLabel from 'domains/core/components/InputLabel';
import { ReservedKeywords } from '../../ReservedKeywords/types';
import { useEffect, useMemo } from 'react';

type Props = {
    autoResponse: SMSAutoResponse;
    keywordsUsed: string[];
    setHasInvalidAutoResponses: (hasInvalidAutoResponses: boolean) => void;
    updateAutoResponse: (autoResponse: SMSAutoResponse) => void;
};

const KeywordsInput = ({ autoResponse, keywordsUsed, setHasInvalidAutoResponses, updateAutoResponse }: Props) => {
    const keywords = autoResponse?.keywords;

    const setKeywords = (keywords: string[]) => {
        let updatedKeywords = keywords;

        if (keywords.length === 0) {
            updatedKeywords = [];
        } else if (keywords?.length > 0) {
            // Only allowing one keyword for now
            updatedKeywords = [keywords[keywords.length - 1]];
        }

        const updatedAutoResponse = {
            ...autoResponse,
            keywords: updatedKeywords,
        };
        updateAutoResponse(updatedAutoResponse);
    };

    const handleInputBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Do nothing if there is no value in the input
        if (!e.target?.value) return;
        // Save keyword when clicking outside input
        setKeywords([...keywords, e.target?.value]);
    };

    const errorMessage = useMemo(() => {
        const alphaNumericRegex = /^[a-z0-9]+$/i;
        const keywordsWithSpecialCharacters = keywords?.find((keyword) => !alphaNumericRegex.test(keyword));
        const reservedKeywordsArray = Object.values(ReservedKeywords);
        const reservedKeywordUsed = keywords.flatMap((keyword) =>
            reservedKeywordsArray.filter((reservedKeyword) => reservedKeyword === keyword.toLowerCase())
        );
        const duplicateKeywords = keywords.flatMap((keyword) =>
            keywordsUsed.filter((usedKeyword) => usedKeyword.toLowerCase() === keyword.toLowerCase())
        );

        let errorMessage = '';
        if (keywordsWithSpecialCharacters) {
            errorMessage = errorMessage + 'Keywords cannot contain special characters. ';
        }
        if (reservedKeywordUsed?.length) {
            errorMessage = errorMessage + 'Reserved keywords cannot be used. ';
        }
        if (duplicateKeywords?.length) {
            errorMessage = errorMessage + 'Keywords must be unique. ';
        }

        return errorMessage;
    }, [keywords, keywordsUsed]);
    const hasError = !!errorMessage;

    useEffect(() => {
        setHasInvalidAutoResponses(hasError);
    }, [hasError, setHasInvalidAutoResponses]);

    return (
        <Autocomplete
            // TODO: remove `disableClearable` when we allow multiple keywords
            disableClearable
            freeSolo
            multiple
            onChange={(_e, newValue) => setKeywords(newValue)}
            onBlur={(e: any) => handleInputBlur(e)}
            options={[]}
            renderTags={(value, getTagProps) =>
                value?.map((option, index) => {
                    if (!option) return null;
                    return (
                        <Chip deleteIcon={<Close />} label={option} {...getTagProps({ index })} sx={greyChipStyles} />
                    );
                })
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    error={hasError}
                    helperText={errorMessage}
                    label={
                        <InputLabel
                            label="Keyword"
                            labelIconTooltipText="Only one keyword per auto-response is supported at this time. Keywords are case insensitive."
                        />
                    }
                />
            )}
            sx={{ marginBottom: hasError ? 4.5 : 2 }}
            value={autoResponse.keywords ?? []}
        />
    );
};

export default KeywordsInput;
